import { maxLength, minLength, required } from '@reliance/forms-js/lib/validation';
import { Configuration } from '@reliance/types/models/Configuration';
import { FORM_ERROR } from 'final-form';
import React, { useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { Form } from 'react-final-form';
import { Select } from '../form/Select';
import { InputType, TextInput } from '../form/TextInput';
import type { FeatureConfigurationKey } from './FeatureCheckboxes';

type FeatureConfigurationModalProps = {
  show: boolean;
  title: string;
  configurationKeys: FeatureConfigurationKey[];
  configurations: Configuration[];
  pendingValues?: Record<string, string>;
  onSave: (values: Record<string, string>) => Promise<{ [FORM_ERROR]: string } | void>;
  onClose: (saved?: boolean) => void;
};

function buildInitialValues(
  configurationKeys: FeatureConfigurationKey[],
  configurations: Configuration[],
  pendingValues: Record<string, string> | undefined
): Record<string, string> {
  const initial: Record<string, string> = {};
  for (const spec of configurationKeys) {
    const row = configurations.find((configuration) => configuration.key === spec.key);
    initial[spec.key] = pendingValues?.[spec.key] ?? row?.value ?? spec.defaultValue ?? '';
  }
  return initial;
}

const FeatureConfigurationModal: React.FC<FeatureConfigurationModalProps> = (props) => {
  const { show, title, configurationKeys, configurations, pendingValues, onSave, onClose } = props;

  const [formInstanceKey, setFormInstanceKey] = useState(0);

  useEffect(() => {
    if (show) {
      setFormInstanceKey((previous) => previous + 1);
    }
  }, [show]);

  const initialValues = useMemo(
    () => buildInitialValues(configurationKeys, configurations, pendingValues),
    [configurationKeys, configurations, pendingValues, formInstanceKey]
  );

  return (
    <Modal show={show} onHide={() => onClose(false)}>
      <Form
        key={formInstanceKey}
        initialValues={initialValues}
        onSubmit={async (formValues: Record<string, string>) => {
          const errorResult = await onSave(formValues);
          if (errorResult != null && typeof errorResult === 'object' && FORM_ERROR in errorResult) {
            return errorResult;
          }
          onClose(true);
          return undefined;
        }}
        render={({ handleSubmit, submitting, hasValidationErrors, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Modal.Header closeButton>
              <Modal.Title>
                {title}
                {submitting && (
                  <>
                    {' '}
                    <i className="fas fa-spin fa-spinner" />
                  </>
                )}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {submitError != null && <Alert variant="danger">{String(submitError)}</Alert>}
              <Row>
                <Col>
                  {configurationKeys.map((spec) => (
                    <div
                      key={spec.key}
                      className="mb-1">
                      {spec.options != null && spec.options.length > 0 ? (
                        <Select
                          label={spec.label}
                          name={spec.key}
                          validationRules={[required]}>
                          {spec.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <TextInput
                          label={spec.label}
                          name={spec.key}
                          type={spec.type ?? InputType.text}
                          validationRules={[required, minLength(1), maxLength(10000)]}
                        />
                      )}
                    </div>
                  ))}
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="default" type="button" disabled={submitting} onClick={() => onClose(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={submitting || hasValidationErrors}>
                {submitting ? 'Saving...' : 'Confirm'}
              </Button>
            </Modal.Footer>
          </form>
        )}
      />
    </Modal>
  );
};

export default FeatureConfigurationModal;
